import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    InputOTP,
} from "../../components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Checkbox } from "../../components/ui/checkbox";
import { Mail, Phone, Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getDeviceFingerprintAsync, getDeviceFingerprint, getAnyStoredDeviceToken, storeDeviceToken } from "../../utils/fingerprint";
import { toast } from "../../hooks/use-toast";
import { translateBackendError } from "../../utils/errors";


export default function Login() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, verify2FA, verifyDeviceOtp, isLoading, user } = useAuth(); // Added verifyDeviceOtp

    const [loginMethod, setLoginMethod] = useState<"email" | "phone">("email");
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [formErrors, setFormErrors] = useState<any>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    // OTP/2FA state
    const [verificationRequired, setVerificationRequired] = useState(false); // Renamed from requires2FA for clarity
    const [requires2FA, setRequires2FA] = useState(false); // New state for actual 2FA
    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [challengeId, setChallengeId] = useState<string | null>(null); // New state for OTP challenge
    const [userId, setUserId] = useState<string | null>(null);
    const [otpCode, setOtpCode] = useState("");

    // Load saved identifier on mount (Remember Me)
    useEffect(() => {
        const savedIdentifier = localStorage.getItem('bousanm_remember_identifier');
        const wasRemembered = localStorage.getItem('bousanm_remember_me') === 'true';
        if (savedIdentifier && wasRemembered) {
            setIdentifier(savedIdentifier);
            setRememberMe(true);
        }
    }, []);



    // Redirect after successful login based on role
    useEffect(() => {
        if (user) {
            const from = (location.state as any)?.from?.pathname;
            const adminRoles = ['Admin', 'SuperAdmin', 'SuperAgent', 'Agent', 'Caissier'];
            const isAdmin = adminRoles.includes(user.role);

            // Show success toast
            toast({
                title: t('auth:success.loginSuccess') + "!",
                description: `${t('auth:login.welcomeBack')} ${user.username || t('common:user')}!`,
                type: "success",
            });

            console.log('🔀 Redirect logic:', { role: user.role, isAdmin, from });

            // Determine destination based on role
            const adminHomeRoute = '/dashboard';
            const userHomeRoute = '/home';

            // Strict redirect logic: Always go to Dashboard (Admins) or Home (Users)
            // We ignore 'from' state (previously visited page) as requested
            if (isAdmin) {
                navigate(adminHomeRoute, { replace: true });
            } else {
                navigate(userHomeRoute, { replace: true });
            }
        }
    }, [user, navigate, location.state, t]);

    const validateForm = () => {
        const newErrors: any = {};
        if (!identifier) {
            newErrors.identifier = loginMethod === "email" ? t('auth:signup.validation.emailRequired') : t('auth:signup.validation.phoneRequired');
        } else if (loginMethod === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
            newErrors.identifier = t('auth:signup.validation.emailInvalid');
        }

        if (password.length !== 4) {
            newErrors.password = t('auth:signup.validation.pin4Digits');
        }

        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async (e?: React.FormEvent | React.MouseEvent) => {
        e?.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setLoginError(null);

        // Handle Remember Me
        if (rememberMe) {
            localStorage.setItem('bousanm_remember_identifier', identifier);
            localStorage.setItem('bousanm_remember_me', 'true');
        } else {
            localStorage.removeItem('bousanm_remember_identifier');
            localStorage.removeItem('bousanm_remember_me');
        }

        try {
            // Timeout fingerprint to prevent hangs on privacy-focused browsers (Brave, Firefox, Safari)
            let fingerprint: string;
            try {
                fingerprint = await Promise.race([
                    getDeviceFingerprintAsync(),
                    new Promise<string>((_, reject) =>
                        setTimeout(() => reject(new Error('Fingerprint timeout')), 2000)
                    ),
                ]);
            } catch {
                console.warn('⚠️ Fingerprint timed out, using sync fallback');
                fingerprint = getDeviceFingerprint(); // Sync fallback
            }
            const storedDeviceToken = getAnyStoredDeviceToken(); // Get stored device token

            const result = await login({
                identifier,
                pin: password,
                deviceFingerprint: fingerprint,
                deviceToken: storedDeviceToken || undefined, // Send stored token for matching
            });

            // If verification required (new device), show OTP UI
            if (result.verificationRequired) {
                setVerificationRequired(true);
                setDeviceId(result.deviceId || null);
                setChallengeId(result.challengeId || null); // Capture challengeId
                return;
            }

            // If 2FA required (Code), show OTP Input UI
            if (result.requires2FA) {
                setRequires2FA(true);
                setDeviceId(result.deviceId || null);
                setUserId(result.userId || null); // Capture userId
                return;
            }
            // Otherwise, useEffect will handle navigation
        } catch (err: any) {
            // Show error to user
            const rawMessage = err.response?.data?.message;
            let message = rawMessage
                ? translateBackendError(rawMessage, i18n.language)
                : t('auth:errors.invalidCredentials');

            // Handle timeout specifically (especially for Brave)
            const isTimeout = err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout');
            if (isTimeout) {
                message = t('auth:errors.timeout') || "Koneksyon an pran twòp tan. Tanpri tcheke entènèt ou.";
            }

            setLoginError(message);
            console.error('Login failed:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerify2FA = useCallback(async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!otpCode || otpCode.length !== 6) {
            // Don't show error immediately while typing, only if auto-submit fails or empty
            return;
        }

        setIsSubmitting(true);
        setLoginError(null);

        try {
            await verify2FA(
                userId!,
                deviceId!,
                otpCode
            );

            // Store device token after successful verification
            if (deviceId && userId) {
                storeDeviceToken(deviceId, userId);
                console.log('✅ Device token stored for future logins');
            }

            // If success, useEffect will redirect
        } catch (error: any) {
            // Handle specific OTP error
            if (error.response?.data?.error === 'INVALID_OR_EXPIRED_CODE') {
                setLoginError(t('auth:deviceVerification.invalidCode'));
            } else if (error.response?.status === 400) {
                setLoginError(t('auth:deviceVerification.approvalMessage')); // Adjusted or new key
            } else {
                const rawMessage = error.response?.data?.message;
                setLoginError(rawMessage ? translateBackendError(rawMessage, i18n.language) : t('common:errors.unknown'));
            }
            setIsSubmitting(false);
            setOtpCode("");
        }
    }, [otpCode, userId, deviceId, verify2FA, t]);

    const handleVerifyDeviceOtp = useCallback(async (code: string) => {
        if (!challengeId || code.length !== 6) return;

        setIsSubmitting(true);
        setLoginError(null);

        try {
            await verifyDeviceOtp(challengeId, code);
            // If success, useEffect will redirect
        } catch (error: any) {
            if (error.response?.data?.error === 'INVALID_OR_EXPIRED_CODE') {
                setLoginError(t('auth:deviceVerification.invalidCode'));
            } else {
                const rawMessage = error.response?.data?.message;
                setLoginError(rawMessage ? translateBackendError(rawMessage, i18n.language) : t('auth:deviceVerification.invalidTokenMessage'));
            }
            setIsSubmitting(false);
            setOtpCode("");
        }
    }, [challengeId, verifyDeviceOtp, t]);

    const handleResendOtp = async () => {
        if (!identifier) return;
        try {
            let fingerprint: string;
            try {
                fingerprint = await Promise.race([
                    getDeviceFingerprintAsync(),
                    new Promise<string>((_, reject) =>
                        setTimeout(() => reject(new Error('Fingerprint timeout')), 2000)
                    ),
                ]);
            } catch {
                fingerprint = getDeviceFingerprint();
            }
            const { authApi } = await import('../../api');
            await authApi.resendDeviceOtp(identifier, fingerprint);
            toast({
                title: t('common:success'),
                description: t('auth:deviceVerification.resendSuccess'),
                type: "success",
            });
        } catch (err) {
            toast({
                title: t('common:error'),
                description: t('common:errors.unknown'),
                type: "error",
            });
        }
    };


    // Auto-submit when OTP is complete
    useEffect(() => {
        if (otpCode.length === 6) {
            if (requires2FA) {
                handleVerify2FA();
            } else if (verificationRequired) {
                handleVerifyDeviceOtp(otpCode);
            }
        }
    }, [otpCode, requires2FA, verificationRequired, handleVerify2FA, handleVerifyDeviceOtp]);

    const toggleLoginMethod = () => {
        setLoginMethod((prev) => (prev === "email" ? "phone" : "email"));
        setIdentifier("");
        setFormErrors({});
    };


    // Device Verification OTP Screen (New device)
    if (verificationRequired && challengeId) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
                <Card className="w-full max-w-[400px] shadow-2xl bg-white/80 backdrop-blur-md border-white/50">
                    <CardHeader className="text-center space-y-2">
                        <div className="mx-auto mb-2 bg-blue-50 p-4 rounded-full w-20 h-20 flex items-center justify-center shadow-inner">
                            <Mail className="h-10 w-10 text-blue-600" />
                        </div>
                        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                            {t('auth:deviceVerification.checkEmail')}
                        </h2>
                        <div className="max-w-[300px] mx-auto text-sm text-gray-500 leading-relaxed">
                            {t('auth:deviceVerification.otpMessage')}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loginError && (
                            <div className="flex items-center gap-2 p-3 mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>{loginError}</span>
                            </div>
                        )}
                        <div className="flex justify-center mb-6">
                            <InputOTP
                                maxLength={6}
                                value={otpCode}
                                onChange={(value) => {
                                    setOtpCode(value);
                                    if (loginError) setLoginError(null);
                                }}
                                pattern={REGEXP_ONLY_DIGITS}
                                disabled={isSubmitting}
                                render={({ slots }) => (
                                    <div className="flex gap-2">
                                        {slots.map((slot, idx) => (
                                            <div
                                                key={idx}
                                                className={`w-10 h-12 flex items-center justify-center text-xl font-bold bg-white rounded-lg border shadow-sm transition-all ${slot.isActive
                                                    ? 'border-primary-500 ring-2 ring-primary-500/20'
                                                    : 'border-gray-200'
                                                    }`}
                                            >
                                                {slot.char ? (
                                                    <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />
                                                ) : null}
                                                {slot.hasFakeCaret && (
                                                    <div className="absolute w-px h-6 bg-primary-600 animate-pulse" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            />
                        </div>

                        {isSubmitting && (
                            <div className="flex items-center justify-center gap-2 text-primary-600 font-medium animate-pulse mb-4">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span>{t('auth:loading.verifying')}</span>
                            </div>
                        )}

                        <div className="text-center space-y-4">
                            <p className="text-xs text-gray-400 max-w-[250px] mx-auto italic">
                                {t('common:checkSpamHint') || "Pa bliye tcheke bwat 'Spam' ou a si ou pa jwenn imèl la."}
                            </p>
                            <button
                                type="button"
                                onClick={handleResendOtp}
                                className="text-sm text-primary-600 hover:text-primary-700 font-semibold transition-colors disabled:opacity-50"
                                disabled={isSubmitting}
                            >
                                {t('auth:deviceVerification.resendCode')}
                            </button>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-2">
                        <Button
                            variant="ghost"
                            className="w-full"
                            onClick={() => {
                                setVerificationRequired(false);
                                setDeviceId(null);
                                setChallengeId(null);
                                setOtpCode("");
                            }}
                        >
                            {t('common:buttons.back')}
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // 2FA TOTP Code Entry Screen
    if (requires2FA && userId && deviceId) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
                <Card className="w-full max-w-[400px] shadow-2xl bg-white/80 backdrop-blur-md border-white/50">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 bg-primary-100 p-3 rounded-full w-16 h-16 flex items-center justify-center">
                            <ShieldCheck className="h-8 w-8 text-primary-600" />
                        </div>
                        <h2 className="text-xl font-bold">{t('auth:login.authentication')}</h2>

                    </CardHeader>
                    <CardContent>
                        {loginError && (
                            <div className="flex items-center gap-2 p-3 mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>{loginError}</span>
                            </div>
                        )}
                        <form onSubmit={handleVerify2FA} className="space-y-6">
                            <div className="flex justify-center">
                                <InputOTP
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(value) => {
                                        setOtpCode(value);
                                        if (loginError) setLoginError(null);
                                    }}
                                    pattern={REGEXP_ONLY_DIGITS}
                                    disabled={isSubmitting}
                                    render={({ slots }) => (
                                        <div className="flex gap-2">
                                            {slots.map((slot, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`w-10 h-12 flex items-center justify-center text-xl font-bold bg-white rounded-lg border shadow-sm transition-all ${slot.isActive
                                                        ? 'border-primary-500 ring-2 ring-primary-500/20'
                                                        : 'border-gray-200'
                                                        }`}
                                                >
                                                    {slot.char ? (
                                                        <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />
                                                    ) : null}
                                                    {slot.hasFakeCaret && (
                                                        <div className="absolute w-px h-6 bg-primary-600 animate-pulse" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                />
                            </div>

                            {/* Loader or spacer */}
                            <div className="h-8 flex items-center justify-center">
                                {isSubmitting && (
                                    <div className="flex items-center gap-2 text-primary-600 font-medium animate-pulse">
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        <span>{t('auth:loading.verifying')}</span>
                                    </div>
                                )}
                            </div>

                        </form>
                    </CardContent>
                    <CardFooter className="justify-center">
                        <Button
                            variant="ghost"
                            className="text-gray-500 hover:text-gray-700"
                            onClick={() => {
                                setRequires2FA(false);
                                setUserId(null);
                                setDeviceId(null);
                                setOtpCode("");
                            }}
                        >
                            {t('common:buttons.back')}
                        </Button>
                    </CardFooter>
                </Card >
            </div >
        );
    }


    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
            <Card className="w-full max-w-[400px] shadow-2xl bg-white/80 backdrop-blur-md border-white/50 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
                <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary/20 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-accent/20 blur-3xl rounded-full pointer-events-none" />
                <CardHeader className="flex flex-col items-center text-center space-y-2">
                    <div className=" bg-primary-50 p-3 rounded-full mb-2">
                        <a href="https://bousanm.com">
                            <img src="/logo.webp" alt="Logo" className="w-16 h-16 object-contain" />
                        </a>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Error Alert */}
                    {loginError && (
                        <div className="flex items-center gap-2 p-3 mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            <span>{loginError}</span>
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div className="grid w-full items-center gap-4">
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="identifier" className="text-left">
                                    {loginMethod === "email" ? t('auth:signup.personalInfo.email') : t('auth:signup.personalInfo.phone')}
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="identifier"
                                        type={loginMethod === "email" ? "text" : "tel"}
                                        placeholder={loginMethod === "email" ? t('auth:login.emailPlaceholder') : t('auth:login.phonePlaceholder')}
                                        value={identifier}
                                        onChange={(e) => {
                                            setIdentifier(e.target.value);
                                            if (formErrors.identifier) setFormErrors({ ...formErrors, identifier: undefined });
                                            if (loginError) setLoginError(null);
                                        }}
                                        required
                                        disabled={isLoading}
                                        className={`pr-14 ${formErrors.identifier ? "border-red-500" : ""}`}
                                    />
                                    <div className="absolute right-0 top-0 h-full flex items-center pr-3">
                                        <Separator orientation="vertical" className="h-6 mr-3" />
                                        <button
                                            type="button"
                                            onClick={toggleLoginMethod}
                                            disabled={isLoading}
                                            className="text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                                            title={loginMethod === "email" ? t('auth:login.phoneLabel') : t('auth:login.phonePlaceholder')} // Search for switch
                                        >
                                            {loginMethod === "email" ? <Phone size={18} /> : <Mail size={18} />}
                                        </button>
                                    </div>
                                </div>
                                {formErrors.identifier && <p className="text-red-500 text-xs mt-1">{formErrors.identifier}</p>}
                            </div>
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="password" className="text-left">{t('auth:login.pinLabel')}</Label>
                                <div className="flex justify-center w-full">
                                    <InputOTP
                                        maxLength={4}
                                        value={password}
                                        onChange={(value) => {
                                            setPassword(value);
                                            if (formErrors.password) setFormErrors({ ...formErrors, password: undefined });
                                            if (loginError) setLoginError(null);
                                        }}
                                        containerClassName="w-full max-w-xs"
                                        pattern={REGEXP_ONLY_DIGITS}
                                        disabled={isLoading || isSubmitting}
                                        render={({ slots }) => (
                                            <div className="flex w-full gap-2 sm:gap-4 justify-between">
                                                {slots.map((slot, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-lg font-bold bg-white rounded-xl shadow-sm transition-all ${slot.isActive
                                                            ? 'border-2 border-primary-600 ring-4 ring-primary-600/20 scale-105'
                                                            : formErrors.password ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-200'
                                                            }`}
                                                    >
                                                        {slot.char ? (
                                                            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-primary-600" />
                                                        ) : null}
                                                        {slot.hasFakeCaret && (
                                                            <div className="absolute w-px h-4 sm:h-5 bg-primary-600 animate-pulse" />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    />
                                </div>
                                {formErrors.password && <p className="text-red-500 text-xs mt-1 text-center">{formErrors.password}</p>}
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t('auth:loading.login')}
                                        </>
                                    ) : (
                                        t('auth:login.loginButton')
                                    )}
                                </Button>
                            </div>
                            <Separator />
                        </div>
                    </form>
                </CardContent>
                <CardFooter className="flex flex-col gap-3 pt-4">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="remember"
                                checked={rememberMe}
                                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                                disabled={isLoading}
                            />
                            <label
                                htmlFor="remember"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                                {t('auth:login.rememberMe')}
                            </label>
                        </div>
                        <a
                            href="/forgot-pin"
                            className="text-sm text-primary-600 hover:text-primary-700 hover:underline font-medium"
                        >
                            {t('auth:login.forgotPin')}
                        </a>
                    </div>

                    <Separator className="my-2" />

                    <div className="text-center text-sm">
                        <span className="text-gray-600">{t('auth:login.noAccount')} </span>
                        <a
                            href="/register"
                            className="text-primary-600 hover:text-primary-700 hover:underline font-semibold"
                        >
                            {t('auth:login.signup')}
                        </a>
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
}

